import { useEffect, useState } from "react";
import { api, type Board, type Session, type Today as TodayData } from "../api.ts";
import { tokens, useTheme } from "../glassTheme.tsx";
import { Segment } from "../components/glass/Segment.tsx";
import { Morning } from "../components/sessions/Morning.tsx";
import { Evening } from "../components/sessions/Evening.tsx";
import { Weekly } from "../components/sessions/Weekly.tsx";
import { Routines } from "./Routines.tsx";
import { Footer } from "./Slots.tsx";
import { longDate } from "../theme.ts";

type Kind = "morning" | "evening" | "weekly";

const OPTIONS: { key: Kind; label: string }[] = [
  { key: "morning", label: "Morning" }, { key: "evening", label: "Evening" }, { key: "weekly", label: "Weekly" },
];

/** Morning before 12:00, evening after, weekly on Sunday from 12:00 (§1) — Europe/Berlin, matching every other date in this app. */
function dueKind(): Kind {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin", weekday: "short", hour: "numeric", hourCycle: "h23",
  }).formatToParts(new Date());
  const isSunday = parts.find((p) => p.type === "weekday")?.value === "Sun";
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  if (isSunday && hour >= 12) return "weekly";
  return hour < 12 ? "morning" : "evening";
}

/**
 * The session that is due (§1) — no other home. `manual` is the segment
 * control's override ("Home switches by time of day... with a small segment
 * control at the top so Karim can pick another. Both, not either" — decided
 * 21.09); it is not persisted, so the screen returns to automatic on the
 * next visit rather than getting stuck on a choice made once.
 */
export function Sessions({
  data, board, onOpen, onChanged, onOpenWorkout,
}: {
  data: TodayData; board: Board;
  onOpen: (id: string) => void; onChanged: () => void;
  onOpenWorkout?: (variant: 1 | 2) => void;
}) {
  const { colors } = useTheme();
  const [manual, setManual] = useState<Kind | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);

  const refetchSessions = () => { api.sessions(7).then(setSessions).catch(() => {}); };
  useEffect(refetchSessions, [data.date]);

  const kind = manual ?? dueKind();
  // Morning has no submit of its own (§1) — only evening and weekly leave a
  // record, so only they can already be "done" for today.
  const doneToday = kind !== "morning" ? sessions.find((s) => s.kind === kind && s.date === data.date) ?? null : null;

  return (
    <div style={{ padding: "12px 16px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div style={{
          fontFamily: tokens.font.serif, fontSize: tokens.type.screenTitle.size,
          lineHeight: tokens.type.screenTitle.lineHeight, color: colors.ink,
        }}>{kind === "morning" ? "Morning" : kind === "evening" ? "Evening" : "Weekly"}</div>
        <div style={{ fontSize: 13, color: colors.ink2 }}>{longDate(data.date)}</div>
      </div>

      <Segment options={OPTIONS} value={kind} onChange={setManual} />

      <div style={{ marginTop: 16 }}>
        {kind === "morning" && <Morning data={data} onOpen={onOpen} onFlagsChanged={onChanged} />}
        {kind === "evening" && (
          <Evening data={data} doneToday={doneToday} onOpen={onOpen} onWritten={() => { refetchSessions(); onChanged(); }} />
        )}
        {kind === "weekly" && (
          <Weekly
            board={board} today={data.date} onOpen={onOpen} doneToday={doneToday}
            onWritten={() => { refetchSessions(); onChanged(); }} onFlagsChanged={onChanged}
          />
        )}
      </div>

      {/* Not part of the new Sessions spec — Routines and the Slots footer
          belong to Body in the redesign, which isn't rebuilt yet. Kept here,
          in their old cockpit styling, on the same call session 17 made:
          the cost of losing a real daily feature outweighs the visual
          inconsistency until Body has a home for them. */}
      <Routines data={data.routines} />
      <Footer slots={data.slots} onChanged={onChanged} onOpenWorkout={onOpenWorkout} />
    </div>
  );
}
